import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { queryKeys } from './queryClient';
import type { UserSummary } from '../types';

/**
 * Fetch every user (active and inactive) for pickers
 *
 * @returns Promise resolving to user summaries ordered by display name
 */
const fetchUsers = async (): Promise<UserSummary[]> => {
  return api.get<UserSummary[]>('/users');
};

/**
 * Hook: Fetch users for the contract owner picker
 *
 * Inactive users are included so a contract's current owner stays selectable
 * after deactivation; callers filter on `isActive` for new assignments.
 *
 * @example
 * ```tsx
 * const { data: users } = useUsers();
 * users?.filter((u) => u.isActive).map((u) => <SelectItem key={u.id} value={u.id}>{u.displayName}</SelectItem>);
 * ```
 */
export const useUsers = () => {
  return useQuery({
    queryKey: queryKeys.users.list(),
    queryFn: fetchUsers,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};
